import {
  Flex,
  Box,
  Heading,
  Tag,
  Text,
  VStack,
  ButtonGroup,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import formatCurrency from '@/lib/formatCurrency';
import { listingTags } from '@/lib/listingUtils';
import { ListingProps } from '@/types/listing';
import { urls } from '@/lib/urls';
import formatHectares from '@/lib/formatHectares';
import { getLocalizedRegionName } from '@/lib/regions';
import formatNumber from '@/lib/formatNumber';
import ButtonLink from '../ButtonLink';

// const LabelAndValue = ({ label, value }: { label: string; value: string }) => (
//   <Flex flexDir="column">
//     <Text fontSize="sm">{label}</Text>
//     <Text fontSize="2xl" fontWeight="600" color="primary.500">
//       {value}
//     </Text>
//   </Flex>
// );

const ListingInfoCard = ({ listing, forestData }: ListingProps) => {
  const { t } = useTranslation('backoffice');
  const tags = listingTags(listing, t);
  const { locale } = useRouter();

  return (
    <Box p={5} background="#F9F8F6" height="100%">
      {tags.map((tag) => (
        <Tag
          textTransform="uppercase"
          size="sm"
          colorScheme="success"
          key={tag}
          mr="2"
          py={1}
        >
          {tag}
        </Tag>
      ))}
      <Heading as="h1" size="lg" fontWeight="600" mb={2}>
        {listing.name}
      </Heading>
      <Text mb={1}>
        {Array.from(
          new Set(
            listing.properties.map((property) =>
              getLocalizedRegionName(property.property_municipality, locale),
            ),
          ),
        ).join(', ')}
      </Text>

      <Text
        textTransform="uppercase"
        fontSize="xs"
        fontWeight="400"
        color="gray.500"
        mb={8}
      >
        {t('listing_number', 'Kohdenumero')}: {listing.public_id}
      </Text>

      <VStack spacing={5} alignItems="flex-start">
        <Flex flexDir="column">
          <Text fontSize="sm">{t('area')}</Text>
          <Text fontSize="2xl" fontWeight="600" color="primary.500">
            {formatHectares(listing.total_area_in_hectares as number, {
              approximate: listing.has_approximate_area,
            })}
          </Text>
        </Flex>
        <Flex flexDir="column">
          <Text fontSize="sm">
            {t('forest_amount_usable', 'Ainespuun määrä')}
          </Text>
          <Text fontSize="2xl" fontWeight="600" color="primary.500">
            {formatNumber(forestData?.combined?.totalVolume, {
              fallbackValue: '-',
              unit: 'm³',
            })}
          </Text>
        </Flex>

        {listing.publish_end_datetime && (
          <Flex flexDir="column">
            <Text fontSize="sm">{t('offer_latest', 'Tarjoa viimeistään')}</Text>
            <Text fontSize="2xl" fontWeight="600" color="primary.500">
              {format(
                new Date(listing.publish_end_datetime as string),
                'd.M.yyyy HH:mm',
              )}
            </Text>
          </Flex>
        )}

        <Flex flexDir="column">
          <Text fontSize="sm">
            {listing.sales_assignment?.price_type === 'request'
              ? t('price_type_request', 'Hintapyyntö')
              : t('price_type_minimum', 'Lähtöhinta')}
          </Text>
          <Text fontSize="3xl" fontWeight="600" color="primary.500">
            {formatCurrency(listing?.total_price as number, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Flex>
      </VStack>
      <ButtonGroup mt={8}>
        <ButtonLink
          link={{
            url: urls.public.listing.offer(listing.public_id ?? ''),
            title: t('make_offer', 'Tee tarjous'),
          }}
          boxShadow="md"
          size="lg"
          colorScheme="primary"
        />
        <ButtonLink
          link={{
            url: `${urls.public.listing.get(listing.public_id ?? '')}#contact`,
            title: t('contact', 'Ota yhteyttä'),
          }}
          boxShadow="md"
          size="lg"
          borderRadius="full"
          colorScheme="success"
          variant="ghost"
        />
      </ButtonGroup>
    </Box>
  );
};

export default ListingInfoCard;
